// Procedural cloud layer — 2D scrolling noise on a skydome, lit by the sun
// Half-res compute pass. No textures — all noise generated in shader.
// Composited in post-process behind scene geometry.

import type { GPU } from "./gpu.js";
import { mat4Invert, type Mat4 } from "./math3d.js";

const CLOUD_SHADER = /* wgsl */ `
struct CloudParams {
  invViewProj: mat4x4f,
  camPos: vec3f, time: f32,
  sunDir: vec3f, sunIntensity: f32,
  screenSize: vec2f, cloudBase: f32, cloudTop: f32,
  windDir: vec2f, coverage: f32, density: f32,
  skyColorZenith: vec3f, _p0: f32,
  skyColorHorizon: vec3f, _p1: f32,
  fogColor: vec3f, _p2: f32,
};

@group(0) @binding(0) var cloudOut: texture_storage_2d<rgba16float, write>;
@group(0) @binding(1) var depthTex: texture_depth_2d;
@group(0) @binding(2) var<uniform> params: CloudParams;

// --- Procedural noise ---

fn hash2(p: vec2f) -> vec2f {
  let k = vec2f(0.3183099, 0.3678794);
  let pp = p * k + k.yx;
  return -1.0 + 2.0 * fract(16.0 * k * fract(pp.x * pp.y * (pp.x + pp.y)));
}

fn gradientNoise(p: vec2f) -> f32 {
  let i = floor(p);
  let f = fract(p);
  let u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(dot(hash2(i + vec2f(0.0, 0.0)), f - vec2f(0.0, 0.0)),
        dot(hash2(i + vec2f(1.0, 0.0)), f - vec2f(1.0, 0.0)), u.x),
    mix(dot(hash2(i + vec2f(0.0, 1.0)), f - vec2f(0.0, 1.0)),
        dot(hash2(i + vec2f(1.0, 1.0)), f - vec2f(1.0, 1.0)), u.x),
    u.y
  );
}

// Worley (cellular) noise — distance to nearest feature point
fn worleyHash(p: vec2f) -> vec2f {
  let n = vec2f(dot(p, vec2f(127.1, 311.7)), dot(p, vec2f(269.5, 183.3)));
  return fract(sin(n) * 43758.5453);
}

fn worleyNoise(uv: vec2f) -> f32 {
  let cell = floor(uv);
  let frac = fract(uv);
  var minDist = 1.0;
  for (var y = -1; y <= 1; y++) {
    for (var x = -1; x <= 1; x++) {
      let neighbor = vec2f(f32(x), f32(y));
      let point = worleyHash(cell + neighbor);
      let diff = neighbor + point - frac;
      minDist = min(minDist, length(diff));
    }
  }
  return minDist;
}

// Fractal Brownian Motion with Worley noise — cloud-like shapes
fn cloudFBM(uv: vec2f) -> f32 {
  var v = 0.0;
  v += (1.0 - worleyNoise(uv * 3.0)) * 0.5;
  v += (1.0 - worleyNoise(uv * 6.0)) * 0.25;
  v += (1.0 - worleyNoise(uv * 12.0)) * 0.125;
  v += gradientNoise(uv * 8.0) * 0.1; // add some perlin for variation
  return v;
}

// Henyey-Greenstein phase function
fn hgPhase(cosTheta: f32, g: f32) -> f32 {
  let g2 = g * g;
  let denom = 1.0 + g2 - 2.0 * g * cosTheta;
  return (1.0 - g2) / (4.0 * 3.14159 * pow(denom, 1.5));
}

// Intersect ray with horizontal slab [yMin, yMax]
fn slabIntersect(origin: vec3f, dir: vec3f, yMin: f32, yMax: f32) -> vec2f {
  if (abs(dir.y) < 0.0001) { return vec2f(-1.0, -1.0); }
  let t0 = (yMin - origin.y) / dir.y;
  let t1 = (yMax - origin.y) / dir.y;
  return vec2f(min(t0, t1), max(t0, t1));
}

@compute @workgroup_size(8, 8)
fn csCloud(@builtin(global_invocation_id) id: vec3u) {
  let halfSize = vec2i(params.screenSize) / 4;
  let coord = vec2i(id.xy);
  if (coord.x >= halfSize.x || coord.y >= halfSize.y) { return; }

  // Reconstruct ray direction
  let uv = (vec2f(coord) + 0.5) / vec2f(halfSize);
  let ndc = vec2f(uv.x * 2.0 - 1.0, 1.0 - uv.y * 2.0);
  let nearClip = params.invViewProj * vec4f(ndc, 0.0, 1.0);
  let farClip = params.invViewProj * vec4f(ndc, 1.0, 1.0);
  let near = nearClip.xyz / nearClip.w;
  let far = farClip.xyz / farClip.w;
  let rayDir = normalize(far - near);

  // Check if scene geometry is closer than the cloud layer
  let depthCoord = coord * 4;
  let depth = textureLoad(depthTex, depthCoord, 0);
  let hasGeo = depth < 0.99999;

  // Sky gradient (always computed — visible between/around clouds)
  let upDot = max(rayDir.y, 0.0);
  var skyGradient = mix(params.skyColorHorizon, params.skyColorZenith, pow(upDot, 0.5));

  // Horizon fog: blend sky toward fogColor near the horizon so it matches
  // the distance fog applied to geometry in the lighting pass.
  // Uses world-space ray direction — camera-tilt independent.
  let horizonBlend = 1.0 - smoothstep(0.0, 0.15, upDot);
  skyGradient = mix(skyGradient, params.fogColor, horizonBlend);

  // Skip cloud march if ray points downward (below horizon)
  // Always output sky color — geometry masking happens at full-res in post-process
  if (rayDir.y < -0.01) {
    textureStore(cloudOut, coord, vec4f(skyGradient, 1.0));
    return;
  }

  // Geometry pixels: output sky color but skip expensive ray march
  if (hasGeo) {
    textureStore(cloudOut, coord, vec4f(skyGradient, 1.0));
    return;
  }

  // Intersect ray with cloud slab
  let slabHit = slabIntersect(params.camPos, rayDir, params.cloudBase, params.cloudTop);
  if (slabHit.x < 0.0 && slabHit.y < 0.0) {
    textureStore(cloudOut, coord, vec4f(skyGradient, 1.0));
    return;
  }

  let tStart = max(slabHit.x, 0.1);
  let tEnd = min(slabHit.y, 800.0); // max cloud distance
  if (tStart >= tEnd) {
    textureStore(cloudOut, coord, vec4f(skyGradient, 1.0));
    return;
  }

  // Ray march through cloud slab
  let sunDir = normalize(params.sunDir);
  let viewDotSun = dot(rayDir, sunDir);
  let phase = hgPhase(viewDotSun, 0.6) * 0.7 + hgPhase(viewDotSun, -0.3) * 0.3; // dual lobe

  let steps = 16u;
  let stepSize = (tEnd - tStart) / f32(steps);

  // Jitter start to reduce banding
  let jitter = fract(sin(dot(vec2f(coord), vec2f(12.9898, 78.233))) * 43758.5453);

  var transmittance = 1.0;
  var lightEnergy = 0.0;

  let wind = params.windDir * params.time;

  for (var i = 0u; i < steps; i++) {
    if (transmittance < 0.01) { break; }

    let t = tStart + (f32(i) + jitter) * stepSize;
    let samplePos = params.camPos + rayDir * t;

    // Height within cloud layer [0..1]
    let heightFrac = clamp((samplePos.y - params.cloudBase) / (params.cloudTop - params.cloudBase), 0.0, 1.0);

    // Sample cloud density from procedural noise
    let cloudUV = samplePos.xz * 0.002 + wind * 0.002;
    var density = cloudFBM(cloudUV);

    // Apply coverage: remap density so low coverage = fewer clouds
    density = smoothstep(1.0 - params.coverage, 1.0, density);

    // Height shaping: clouds are round on top, flat on bottom (cumulus-like)
    let heightShape = smoothstep(0.0, 0.15, heightFrac) * smoothstep(1.0, 0.6, heightFrac);
    density *= heightShape * params.density;

    if (density < 0.01) { continue; }

    // Beer-Lambert extinction
    let extinction = density * stepSize * 0.02;
    let beer = exp(-extinction);

    // Light march: 2 steps toward sun (cheap single-octave noise for light samples)
    var sunTransmittance = 1.0;
    let lightStepSize = (params.cloudTop - samplePos.y) / (sunDir.y + 0.001) / 2.0;
    for (var s = 0u; s < 2u; s++) {
      let lightPos = samplePos + sunDir * f32(s + 1u) * max(lightStepSize, 8.0);
      let lHeightFrac = clamp((lightPos.y - params.cloudBase) / (params.cloudTop - params.cloudBase), 0.0, 1.0);
      if (lHeightFrac < 0.0 || lHeightFrac > 1.0) { break; }
      let lUV = lightPos.xz * 0.002 + wind * 0.002;
      // Single octave for light march — much cheaper, shadow detail isn't critical
      var lDensity = 1.0 - worleyNoise(lUV * 3.0);
      lDensity = smoothstep(1.0 - params.coverage, 1.0, lDensity);
      let lShape = smoothstep(0.0, 0.15, lHeightFrac) * smoothstep(1.0, 0.6, lHeightFrac);
      lDensity *= lShape * params.density;
      sunTransmittance *= exp(-lDensity * max(lightStepSize, 8.0) * 0.02);
    }

    // Multi-scattering approximation (3 octaves — brightens cloud interiors)
    var msLight = 0.0;
    var msExt = extinction;
    var msContrib = 1.0;
    var msG = 0.6;
    for (var m = 0u; m < 3u; m++) {
      msLight += sunTransmittance * hgPhase(viewDotSun, msG) * msContrib;
      msExt *= 0.5;
      msContrib *= 0.5;
      msG *= 0.5;
    }

    // Powder effect: darkens thin cloud edges when backlit
    let powder = 1.0 - exp(-density * stepSize * 0.04);
    let powderTerm = mix(1.0, powder, clamp(-viewDotSun * 0.5 + 0.5, 0.0, 1.0));

    lightEnergy += msLight * powderTerm * transmittance * (1.0 - beer) * params.sunIntensity;
    transmittance *= beer;
  }

  // Color: sun-lit clouds are warm, shadow areas are cool sky blue
  let sunColor = vec3f(1.0, 0.95, 0.88);
  let ambientColor = mix(params.skyColorHorizon, params.skyColorZenith, 0.3) * 0.4;
  let cloudColor = sunColor * lightEnergy + ambientColor * (1.0 - transmittance);

  // Blend clouds with sky gradient (skyGradient already has horizon fog baked in)
  let finalColor = skyGradient * transmittance + cloudColor;

  textureStore(cloudOut, coord, vec4f(finalColor, 1.0));
}
`;

export type CloudConfig = {
  cloudBase: number;
  cloudTop: number;
  coverage: number;
  density: number;
  windSpeed: number;
  windAngle: number;
  skyColorZenith: [number, number, number];
  skyColorHorizon: [number, number, number];
};

export const DEFAULT_CLOUD_CONFIG: CloudConfig = {
  cloudBase: 85,
  cloudTop: 140,
  coverage: 0.74,
  density: 1.2,
  windSpeed: 3.0,
  windAngle: 0.8,
  skyColorZenith: [0.145, 0.200, 0.337],
  skyColorHorizon: [0.106, 0.149, 0.192],
};

export type CloudParams = {
  viewProj: Float32Array;
  camPos: [number, number, number];
  sunDir: [number, number, number];
  sunIntensity: number;
  time: number;
  fogColor: [number, number, number];
};

export class Clouds {
  output: GPUTexture;
  config: CloudConfig;

  private pipeline: GPUComputePipeline;
  private bindGroup: GPUBindGroup;
  private uniformBuffer: GPUBuffer;
  private uniformData = new Float32Array(52);
  private halfW: number;
  private halfH: number;

  constructor(private gpu: GPU, depthTexture: GPUTexture, config: CloudConfig = DEFAULT_CLOUD_CONFIG) {
    const { device, canvas } = gpu;
    this.config = { ...config };
    this.halfW = Math.ceil(canvas.width / 4);
    this.halfH = Math.ceil(canvas.height / 4);

    this.output = device.createTexture({
      size: [this.halfW, this.halfH],
      format: "rgba16float",
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });

    // 13 vec4s = 52 floats = 208 bytes
    this.uniformBuffer = device.createBuffer({ size: 208, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    const module = device.createShaderModule({ code: CLOUD_SHADER });
    const layout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "depth" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      ],
    });

    this.pipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      compute: { module, entryPoint: "csCloud" },
    });

    this.bindGroup = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: this.output.createView() },
        { binding: 1, resource: depthTexture.createView() },
        { binding: 2, resource: { buffer: this.uniformBuffer } },
      ],
    });
  }

  compute(encoder: GPUCommandEncoder, p: CloudParams): void {
    const c = this.config;
    const invViewProj = mat4Invert(p.viewProj);
    const windX = Math.cos(c.windAngle) * c.windSpeed;
    const windZ = Math.sin(c.windAngle) * c.windSpeed;

    const d = this.uniformData;
    d.set(invViewProj, 0);
    // camPos + time
    d[16] = p.camPos[0]; d[17] = p.camPos[1]; d[18] = p.camPos[2]; d[19] = p.time;
    // sunDir + sunIntensity
    const sunLen = Math.sqrt(p.sunDir[0]**2 + p.sunDir[1]**2 + p.sunDir[2]**2);
    d[20] = sunLen > 0.001 ? p.sunDir[0]/sunLen : 0;
    d[21] = sunLen > 0.001 ? p.sunDir[1]/sunLen : 1;
    d[22] = sunLen > 0.001 ? p.sunDir[2]/sunLen : 0;
    d[23] = p.sunIntensity;
    // screenSize + cloudBase + cloudTop
    d[24] = this.gpu.canvas.width; d[25] = this.gpu.canvas.height; d[26] = c.cloudBase; d[27] = c.cloudTop;
    // windDir + coverage + density
    d[28] = windX; d[29] = windZ; d[30] = c.coverage; d[31] = c.density;
    // skyColorZenith
    d[32] = c.skyColorZenith[0]; d[33] = c.skyColorZenith[1]; d[34] = c.skyColorZenith[2];
    // skyColorHorizon
    d[36] = c.skyColorHorizon[0]; d[37] = c.skyColorHorizon[1]; d[38] = c.skyColorHorizon[2];
    // fogColor
    d[40] = p.fogColor[0]; d[41] = p.fogColor[1]; d[42] = p.fogColor[2];

    this.gpu.device.queue.writeBuffer(this.uniformBuffer, 0, d);

    const pass = encoder.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.dispatchWorkgroups(Math.ceil(this.halfW / 8), Math.ceil(this.halfH / 8));
    pass.end();
  }
}
